// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the body of an automation review card (prepare_flow, prepare_native_action,
// prepare_scheduled_message) and of a running automation's approval card. The steps are what
// the user authorizes, so their text is shown in full; the raw definition is never drawn. A
// one-time action with one mail step reads as an email (what it does, who it goes to, the
// letter, a status chip) and its words can be edited in the card before Confirm.

import styles from '@app/features/konvo_assistant/components/AssistantActionCard.module.css';
import {ruleText} from '@app/features/konvo_scheduled/components/RepeatRuleText';
import {agentPlaceKey} from '@app/features/konvo_scheduled/utils/ScheduledView';
import {
	ArrowBendDoubleUpLeftIcon,
	ArrowBendUpLeftIcon,
	ClockIcon,
	EnvelopeSimpleIcon,
	NotePencilIcon,
	PaperPlaneIcon,
	ShareFatIcon,
} from '@app/features/ui/icons/Icons';
import type {AssistantFlowCardView} from '@konvo/schema/src/domains/konvo/AssistantFlow';
import type {I18n, MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';

type FlowCardStep = AssistantFlowCardView['steps'][number];

const MAIL_ACTIONS = new Set(['reply_mail', 'send_mail', 'edit_mail_draft', 'send_mail_draft']);

const KIND_AUTOMATION = msg({
	message: 'Automation',
	comment: 'Assistant review card: kind badge for an automation or a one-time action run as an automation.',
});
const KIND_EMAIL = msg({message: 'Email', comment: 'Assistant review card: kind badge for an email action.'});

const STEP_TITLES: Record<string, MessageDescriptor> = {
	reply: msg({message: 'Reply in the thread', comment: 'Automation review card: a step that replies to an email.'}),
	reply_all: msg({
		message: 'Reply all in the thread',
		comment: 'Automation review card: a step that replies to everyone on an email.',
	}),
	forward: msg({message: 'Forward email', comment: 'Automation review card: a step that forwards an email.'}),
	send_mail: msg({message: 'Send email', comment: 'Automation review card: step title.'}),
	edit_mail_draft: msg({message: 'Edit email draft', comment: 'Automation review card: step title.'}),
	send_mail_draft: msg({message: 'Send email draft', comment: 'Automation review card: step title.'}),
	discard_mail_draft: msg({message: 'Discard email draft', comment: 'Automation review card: step title.'}),
	send_message: msg({message: 'Send message', comment: 'Automation review card: step title.'}),
	send_dashboard: msg({message: 'Send dashboard', comment: 'Automation review card: step title.'}),
	export_report: msg({message: 'Export a report', comment: 'Automation review card: step title.'}),
	create_task: msg({message: 'Create task', comment: 'Automation review card: step title.'}),
	update_task: msg({message: 'Update task', comment: 'Automation review card: step title.'}),
	comment_task: msg({message: 'Comment on task', comment: 'Automation review card: step title.'}),
	create_calendar_event: msg({message: 'Create calendar event', comment: 'Automation review card: step title.'}),
	connector_action: msg({message: 'Run connected API action', comment: 'Automation review card: step title.'}),
	start_agent_thread: msg({
		message: 'Start a coding-agent thread',
		comment: 'Automation review card: step title; each run starts a new thread on the person’s coding agent.',
	}),
	prompt_agent_thread: msg({
		message: 'Send a prompt to a coding agent',
		comment: 'Automation review card: step title; each run sends the prompt to an existing coding-agent thread.',
	}),
	summarize: msg({message: 'Create summary', comment: 'Automation review card: step title.'}),
	wait: msg({message: 'Wait', comment: 'Automation review card: step title.'}),
	branch: msg({message: 'Check conditions', comment: 'Automation review card: step title.'}),
	for_each: msg({message: 'Repeat for each item', comment: 'Automation review card: step title.'}),
};

const SAVES_TO_DRAFTS = msg({
	message: 'Saves to Outlook Drafts',
	comment: 'Email review card: status chip; confirming only saves the reply as a draft in Outlook, nothing is sent.',
});
const UPDATES_DRAFT = msg({
	message: 'Updates your Outlook draft',
	comment: 'Email review card: status chip; confirming changes an existing draft in Outlook, nothing is sent.',
});
const CC = msg({message: 'Cc {people}', comment: 'Email review card: copied recipients; {people} is a list.'});
const SAVED_AS_DRAFT = msg({
	message: 'Saved to your Outlook Drafts, not sent',
	comment: 'Automation review card: the reply is only saved as a draft in Outlook.',
});
const SENDS_ON_CONFIRM = msg({
	message: 'Sends when you confirm',
	comment: 'Automation review card: the email goes out as soon as the user confirms.',
});
const SENDS_AT = msg({
	message: 'Sends at {time}',
	comment: 'Automation review card: a scheduled email; {time} is a date and time.',
});
const TO = msg({message: 'To {people}', comment: 'Automation review card: recipients of a step; {people} is a list.'});
const RUNS_ONCE = msg({
	message: 'Runs once when you confirm',
	comment: 'Automation review card: a one-time automation.',
});
const SCHEDULED = msg({
	message: 'Scheduled for {time}',
	comment: 'Automation review card: when a scheduled automation first runs; {time} is a date and time.',
});
const REPEATS: Record<string, MessageDescriptor> = {
	daily: msg({message: 'Every day', comment: 'Automation review card: how often a scheduled automation repeats.'}),
	weekdays: msg({
		message: 'Every weekday',
		comment: 'Automation review card: how often a scheduled automation repeats.',
	}),
	weekly: msg({message: 'Every week', comment: 'Automation review card: how often a scheduled automation repeats.'}),
	monthly: msg({message: 'Every month', comment: 'Automation review card: how often a scheduled automation repeats.'}),
};
const ON_EVENT = msg({
	message: 'Runs when: {event}',
	comment: 'Automation review card: an automation started by an event; {event} names the event.',
});
const NEW_WORKTREE = msg({
	message: 'New worktree each run',
	comment: 'Automation review card: each run of the coding-agent step works in a new git worktree.',
});
const APPROVAL = msg({
	message: 'A running automation asks to take these next steps',
	comment: 'Automation approval card: explains what approving does.',
});
const STEPS = msg({message: 'Steps', comment: 'Automation review card: label above the list of steps.'});
const SAVE_DRAFT = msg({
	message: 'Save draft',
	comment: 'Automation review card: confirm button when the action only saves an email draft.',
});
const SEND_REPLY = msg({message: 'Send reply', comment: 'Automation review card: confirm button that sends a reply.'});
const SEND_EMAIL = msg({message: 'Send email', comment: 'Email review card: confirm button that sends an email.'});
const RUN_ONCE = msg({message: 'Run once', comment: 'Automation review card: confirm button for a one-time action.'});
const TURN_ON = msg({
	message: 'Turn on',
	comment: 'Automation review card: confirm button that authorizes a scheduled or event automation.',
});
const APPROVE = msg({message: 'Approve', comment: 'Automation approval card: confirm button.'});

function humanize(value: string): string {
	const text = value.replaceAll('_', ' ');
	return text.charAt(0).toUpperCase() + text.slice(1);
}

function formatTime(value: string): string {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat(undefined, {
		weekday: 'short',
		day: 'numeric',
		month: 'short',
		hour: '2-digit',
		minute: '2-digit',
	}).format(date);
}

function strings(value: unknown): Array<string> {
	return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

/** A one-time card with a single mail step reads as an email card, not an automation. */
function singleMailStep(view: AssistantFlowCardView): FlowCardStep | null {
	const only = view.steps.length === 1 ? view.steps[0] : null;
	const once = view.kind === 'approval' || view.trigger?.kind === 'manual';
	return only && once && only.action && MAIL_ACTIONS.has(only.action) ? only : null;
}

export function flowKindLabel(i18n: I18n, view: AssistantFlowCardView): string {
	return i18n._(singleMailStep(view) ? KIND_EMAIL : KIND_AUTOMATION);
}

export function isFlowMailCard(view: AssistantFlowCardView): boolean {
	return singleMailStep(view) != null;
}

export function flowConfirmLabel(i18n: I18n, view: AssistantFlowCardView): string {
	if (view.kind === 'approval') return i18n._(APPROVE);
	const mail = singleMailStep(view);
	if (mail?.action === 'reply_mail') return i18n._(mail.args.send === true ? SEND_REPLY : SAVE_DRAFT);
	if (mail?.action === 'edit_mail_draft') return i18n._(SAVE_DRAFT);
	if (mail) return i18n._(SEND_EMAIL);
	return i18n._(view.trigger?.kind === 'manual' ? RUN_ONCE : TURN_ON);
}

/** The words of a one-time email the user can change before Confirm, or null when it has none. */
export interface FlowMailFields {
	text: string;
	/** Null when the email keeps the thread's subject (replies) or the draft's own. */
	subject: string | null;
}

export function flowMailFields(view: AssistantFlowCardView): FlowMailFields | null {
	const mail = view.kind === 'automation' ? singleMailStep(view) : null;
	if (!mail || typeof mail.args.html !== 'string') return null;
	const subject =
		(mail.action === 'send_mail' || mail.action === 'edit_mail_draft') && typeof mail.args.subject === 'string'
			? mail.args.subject
			: null;
	return {text: mail.text ?? '', subject};
}

function mailStatus(i18n: I18n, step: FlowCardStep): {label: string; send: boolean; Icon: typeof ClockIcon} {
	const sendAt = typeof step.args.send_at === 'string' && step.args.send_at ? step.args.send_at : null;
	if (step.action === 'edit_mail_draft') return {label: i18n._(UPDATES_DRAFT), send: false, Icon: NotePencilIcon};
	if (step.action === 'reply_mail' && step.args.send !== true)
		return {label: i18n._(SAVES_TO_DRAFTS), send: false, Icon: NotePencilIcon};
	if (sendAt) return {label: i18n._(SENDS_AT, {time: formatTime(sendAt)}), send: true, Icon: ClockIcon};
	return {label: i18n._(SENDS_ON_CONFIRM), send: true, Icon: PaperPlaneIcon};
}

function mailIcon(step: FlowCardStep) {
	if (step.action !== 'reply_mail') return EnvelopeSimpleIcon;
	if (step.args.mode === 'forward') return ShareFatIcon;
	return step.args.mode === 'reply_all' ? ArrowBendDoubleUpLeftIcon : ArrowBendUpLeftIcon;
}

/** A one-time email: what it does and its status, who it goes to, then the letter itself. */
function MailSummary({view, step, editing}: {view: AssistantFlowCardView; step: FlowCardStep; editing: boolean}) {
	const {i18n} = useLingui();
	const status = mailStatus(i18n, step);
	const Icon = mailIcon(step);
	const subject = typeof step.args.subject === 'string' && step.args.subject ? step.args.subject : null;
	const to = strings(step.args.to);
	const cc = strings(step.args.cc);
	const people = [
		to.length > 0 ? i18n._(TO, {people: to.join(', ')}) : null,
		cc.length > 0 ? i18n._(CC, {people: cc.join(', ')}) : null,
	].filter(Boolean);
	return (
		<>
			<span className={styles.title}>{view.title}</span>
			<span className={styles.mailFacts}>
				<span className={styles.mailWhat}>
					<Icon size={14} />
					{[stepTitle(i18n, step), subject].filter(Boolean).join(' · ')}
				</span>
				<span
					className={clsx(styles.mailStatus, status.send && styles.mailStatusSend)}
					data-flx="konvo-assistant.flow-card.mail-status"
				>
					<status.Icon size={13} />
					{status.label}
				</span>
			</span>
			{people.length > 0 && <span className={styles.meta}>{people.join(' · ')}</span>}
			{step.text && !editing && (
				<div className={styles.letter} data-flx="konvo-assistant.flow-card.text">
					{step.text}
				</div>
			)}
		</>
	);
}

function stepTitle(i18n: I18n, step: FlowCardStep): string {
	if (step.title) return step.title;
	const key = step.action === 'reply_mail' ? String(step.args.mode ?? 'reply') : (step.action ?? step.kind);
	const known = STEP_TITLES[key];
	return known ? i18n._(known) : humanize(step.action ?? step.kind);
}

function stepMeta(i18n: I18n, step: FlowCardStep, destinations: Record<string, string>): string | null {
	const parts: Array<string> = [];
	const to = strings(step.args.to);
	if (to.length > 0) parts.push(i18n._(TO, {people: to.join(', ')}));
	if (typeof step.args.destination_id === 'string')
		parts.push(i18n._(TO, {people: destinations[step.args.destination_id] ?? step.args.destination_id}));
	if (typeof step.args.subject === 'string' && step.args.subject) parts.push(step.args.subject);
	if (step.action === 'reply_mail') {
		if (step.args.send !== true) parts.push(i18n._(SAVED_AS_DRAFT));
		else if (typeof step.args.send_at === 'string') parts.push(i18n._(SENDS_AT, {time: formatTime(step.args.send_at)}));
		else parts.push(i18n._(SENDS_ON_CONFIRM));
	}
	if (step.action === 'create_calendar_event' && typeof step.args.start === 'string')
		parts.push(formatTime(step.args.start));
	const agentPlace = agentPlaceKey({action: step.action ?? undefined, args: step.args});
	if (agentPlace) {
		parts.push(destinations[agentPlace] ?? String(step.args.machine_id));
		const model = destinations[agentModelKey(String(step.args.machine_id), step.args.model)];
		if (model) parts.push(model);
		if (step.args.worktree === true) parts.push(i18n._(NEW_WORKTREE));
	}
	return parts.length > 0 ? parts.join(' · ') : null;
}

/** The key of an agent step's model label, as AgentsFlowActions.agentModelKey builds it. */
function agentModelKey(machineId: string, model: unknown): string {
	if (!model || typeof model !== 'object') return `agent_model:${machineId}:default`;
	const m = model as Record<string, unknown>;
	return `agent_model:${machineId}:${[m.provider, m.model, m.effort ?? '', m.serviceTier ?? ''].map(String).join('/')}`;
}

function triggerLine(i18n: I18n, view: AssistantFlowCardView): string | null {
	if (view.kind === 'approval') return i18n._(APPROVAL);
	const trigger = view.trigger;
	if (!trigger) return null;
	if (trigger.kind === 'manual') return i18n._(RUNS_ONCE);
	if (trigger.kind === 'event') return i18n._(ON_EVENT, {event: humanize(trigger.event)});
	const when = i18n._(SCHEDULED, {time: `${formatTime(trigger.at)} (${trigger.timezone})`});
	if (trigger.repeat) return `${when} · ${ruleText(i18n, trigger.repeat, trigger.at)}`;
	const repeats = trigger.recurrence ? REPEATS[trigger.recurrence] : null;
	return repeats ? `${when} · ${i18n._(repeats)}` : when;
}

export function FlowSummary({view, editing = false}: {view: AssistantFlowCardView; editing?: boolean}) {
	const {i18n} = useLingui();
	const mail = singleMailStep(view);
	if (mail) return <MailSummary view={view} step={mail} editing={editing} />;
	const trigger = triggerLine(i18n, view);
	return (
		<>
			<span className={styles.title}>{view.title}</span>
			{trigger && <span className={styles.meta}>{trigger}</span>}
			{view.steps.length > 0 && (
				<>
					<span className={styles.agentLabel}>{i18n._(STEPS)}</span>
					<ol className={styles.flowSteps} data-flx="konvo-assistant.flow-card.steps">
						{view.steps.map((step, index) => {
							const meta = stepMeta(i18n, step, view.destinations);
							return (
								<li key={index}>
									<span className={styles.flowStepTitle}>{stepTitle(i18n, step)}</span>
									{meta && <span className={styles.meta}>{meta}</span>}
									{step.text &&
										(agentPlaceKey({action: step.action ?? undefined, args: step.args}) ? (
											// The whole prompt: each run sends it to the agent without another card.
											<span className={styles.agentText} data-flx="konvo-assistant.flow-card.agent-prompt">
												{step.text}
											</span>
										) : (
											<span className={styles.preview}>{step.text}</span>
										))}
								</li>
							);
						})}
					</ol>
				</>
			)}
		</>
	);
}
